import { EventEmitter, Injectable, Output } from '@angular/core';
import { ContentTooltip } from 'src/app/models/content-tooltip';
import { ContentText } from '../../models/content-text';
import { DialogOptions } from '../../models/dialog-options';
import { NewPlanOption } from '../../models/new-plan-option';
import { OwnerInfo } from '../../models/owner-info';
import { VehicleDetail } from '../../models/vehicle-detail';

// singleton service - don't include it in any component's providers []
@Injectable({ providedIn: 'root' })
export class SharedStateService {
    @Output() onToggleLoader = new EventEmitter<boolean>();
    @Output() onShowDialog = new EventEmitter<DialogOptions>();
    @Output() onShowStart = new EventEmitter<void>();
    @Output() onShowEnterCustomerDetails = new EventEmitter<void>();
    @Output() onShowCustomerDetails = new EventEmitter<void>();
    @Output() onShowSelectPlan = new EventEmitter<void>();
    @Output() onShowViewQuote = new EventEmitter<void>();
    @Output() onShowJustBrowsingVehicleDetails = new EventEmitter<void>();
    @Output() onContentTextLoaded = new EventEmitter<void>();
    @Output() onContentTooltipLoaded = new EventEmitter<void>();

    ownerInfo: OwnerInfo;
    selectedVehicleDetail: VehicleDetail
    newPlanOption: NewPlanOption;
    enterCustomerDetailsErrorCount = 0;
    justBrowsing = false;

    _contentTexts: ContentText[] = [];
    get contentTexts(): ContentText[] {
        return this._contentTexts;
    }
    set contentTexts(value: ContentText[]) {
        this._contentTexts = value;
        this.onContentTextLoaded.emit();
    }

    _contentTooltips: ContentTooltip[] = [];
    get contentTooltips(): ContentTooltip[] {
        return this._contentTooltips;
    }
    set contentTooltips(value: ContentTooltip[]) {
        this._contentTooltips = value;
        this.onContentTooltipLoaded.emit();
    }

    constructor() {
        //console.log('SharedStateService.constructor');
        this.loadState();
    }

    toggleLoader(isVisible: boolean): void {
        this.onToggleLoader.emit(isVisible);
    }

    showDialog(dialogOptions: DialogOptions): void {
        this.onShowDialog.emit(dialogOptions);
    }

    setOwnerInfo(ownerInfo: OwnerInfo): void {
        this.ownerInfo = ownerInfo;
        this.enterCustomerDetailsErrorCount = 0;
    }

    saveState(): void {
        //console.log('saving state');
        this.setsessionStorageItem(this.ownerInfo, 'ownerInfo');
        this.setsessionStorageItem(this.selectedVehicleDetail, 'selectedVehicleDetail');
        this.setsessionStorageItem(this.newPlanOption, 'newPlanOption');
        this.setsessionStorageItem(this.justBrowsing, 'justBrowsing');
    }

    loadState(): void {
        //console.log('loading saved state');
        this.ownerInfo = this.getsessionStorageItem('ownerInfo');
        this.selectedVehicleDetail = this.getsessionStorageItem('selectedVehicleDetail');
        this.newPlanOption = this.getsessionStorageItem('newPlanOption');
        this.justBrowsing = this.getsessionStorageItem('justBrowsing');
    }

    clearState(): void {
        //console.log('clearing saved state');
        this.ownerInfo = undefined;
        this.selectedVehicleDetail = undefined;
        this.newPlanOption = undefined;
        this.enterCustomerDetailsErrorCount = 0;
        sessionStorage.clear();
    }

    showStart(): void {
        this.onShowStart.emit();
    }

    showEnterCustomerDetails(clearState: boolean): void {
        if (clearState) {
            this.clearState();
        }
        this.onShowEnterCustomerDetails.emit();
    }

    showCustomerDetails(): void {
        this.saveState();
        this.onShowCustomerDetails.emit();
    }

    showSelectPlan(): void {
        this.saveState();
        this.onShowSelectPlan.emit();
    }

    showViewQuote(): void {
        this.saveState();
        this.onShowViewQuote.emit();
    }

    showJustBrowsingVehicleDetails(): void {
        this.onShowJustBrowsingVehicleDetails.emit();
    }

    private setsessionStorageItem(item: unknown, key: string) {
        if (item) {
            //console.log('saving state for: ' + key);
            sessionStorage.setItem(key, JSON.stringify(item));
        }
    }

    private getsessionStorageItem(key: string): any {
        const storageItem = sessionStorage.getItem(key);
        if (storageItem) {
            //console.log('loading from saved state: ' + key);
            return JSON.parse(storageItem);
        } else {
            //console.log('could not load from saved state: ' + key);
            return undefined;
        }
    }
}
